import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { SITE, IMAGES } from "@/data/content";
import { useContent } from "@/context/ContentContext";
import { MaskLine } from "@/components/site/Reveal";

export const Hero = () => {
  const CONTENT = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yBlob = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28">
      <motion.div
        style={{ y: yBlob }}
        className="pointer-events-none absolute -top-24 -right-24 w-[36rem] h-[36rem] rounded-full bg-rose-100/70 blur-3xl"
      />
      <motion.div
        style={{ y: yBlob }}
        className="pointer-events-none absolute top-40 -left-40 w-[30rem] h-[30rem] rounded-full bg-pink-50 blur-3xl"
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-rose-700" />
            <span className="text-xs uppercase tracking-[0.25em] text-rose-800">
              Psicóloga Clínica · {CONTENT.origin}
            </span>
          </motion.div>

          <h1 className="font-display font-light leading-[0.92] text-stone-900 text-6xl sm:text-7xl lg:text-8xl">
            <MaskLine delay={0.1}>Um espaço</MaskLine>
            <MaskLine delay={0.22} className="italic text-rose-800">
              seguro
            </MaskLine>
            <MaskLine delay={0.34}>para se</MaskLine>
            <MaskLine delay={0.46} className="italic">
              (re)encontrar
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-9 max-w-md text-stone-600 text-base leading-relaxed"
          >
            Acompanhamento psicológico para crianças, adolescentes e adultos —
            presencial, online ou ao domicílio.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/marcacao"
              data-testid="hero-book-btn"
              className="btn-rose group inline-flex items-center gap-2 rounded-full bg-rose-800 text-white px-8 py-4 text-sm"
            >
              Agendar marcação
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <a
              href="#sobre"
              data-testid="hero-about-link"
              className="link-underline text-sm text-stone-700"
            >
              Conhecer a Inês
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            data-testid="hero-trust-note"
            className="mt-4 text-xs text-stone-400"
          >
            Confirmação imediata por email · Pagamento no local · Sem compromisso
          </motion.p>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-[2rem] bg-rose-50 aspect-[4/5] shadow-[0_30px_80px_-40px_rgba(159,18,57,0.4)]">
              <motion.img
                style={{ y: yImg }}
                src={IMAGES.portrait}
                alt={CONTENT.fullName}
                className="absolute inset-0 w-full h-[112%] object-cover object-top"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl px-6 py-4 shadow-lg border border-rose-50">
              <p className="font-display text-xl text-stone-900">{CONTENT.fullName}</p>
              <p className="text-xs text-stone-500 mt-0.5">Psicóloga Clínica</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
